<template>
  <div
    class="listing text-center"
    v-bind="$attrs"
  >
    <div class="unit-listing">
      <div class="details-container">
        <div class="unit-rent">
          <strong class="text-2xl">{{ USD.format(unit.market_rent) }}</strong>
          / month
        </div>
        <div class="unit-details">
          <span v-if="unit.bedrooms === 'Storage'">
            <i
              class="fas fa-warehouse fa-detail"
              role="none"
            ></i>
            <span class="text-xl">{{ unit.bedrooms }}</span>
          </span>
          <span v-else>
            <i
              class="fas fa-bed fa-detail"
              role="none"
            ></i>
            <span
              class="text-xl"
              v-if="unit.bedrooms === 'Studio'"
              >{{ unit.bedrooms }}</span
            >
            <span v-else
              ><span class="text-xl">{{ unit.bedrooms }}</span> Bed(s)</span
            >
          </span>
          <span v-if="unit.bedrooms !== 'Storage'">
            <i
              class="fas fa-bath fa-detail"
              role="none"
            ></i
            ><span class="text-xl">{{ unit.bathrooms }}</span> Bath(s)</span
          >
          <span>
            <i
              class="fas fa-ruler-combined fa-detail"
              role="none"
            ></i
            ><span class="text-xl">{{ unit.sqft ? Math.round(parseFloat(unit.sqft)) : "" }}</span> Sq ft
          </span>
        </div>
        <div class="address-container">
          <span class="mb-2"
            ><i
              class="fas fa-map-marker-alt fa-detail"
              role="none"
            ></i>
            {{ unit.unit_full_address }}</span
          >
        </div>
      </div>

      <div
        class="img-container"
        v-if="unit.photos.length"
      >
        <img
          :src="unit.photos[0].Url"
          :alt="unit.unit_full_address"
          class="unit-img"
        />
        <div
          class="img-mask"
          @click="showImages = !showImages"
        >
          <div class="mask-icon-group">
            <div class="mask-icon">
              <i class="pi pi-images"></i>
            </div>
            <div class="mask-text">
              <p>View More</p>
            </div>
          </div>
        </div>
        <Galleria
          v-model:visible="showImages"
          fullScreen
          :value="unit.photos"
          :showThumbnails="true"
          :showItemNavigators="true"
          :circular="true"
          containerStyle="max-width: 60%"
          :numVisible="7"
          :responsiveOptions="responsiveOptions"
        >
          <template #item="slotProps">
            <img
              :src="slotProps.item.Url"
              style="max-height: 70vh; max-width: 95vw; height: auto; width: auto"
            />
          </template>
          <template #thumbnail="slotProps">
            <img
              :src="slotProps.item.ThumbnailUrl"
              style="max-width: 100px"
            />
          </template>
        </Galleria>
      </div>
      <div class="unit-title">
        <p>{{ unit.marketing_title }}</p>
        <div class="showing-btns">
          <div class="btn-container">
            <Button
              v-if="unit.auto_showing"
              @click="trackSelfShowing"
              class="p-button-secondary mr-2 mb-2"
              label="Enter Property Yourself"
            ></Button>
          </div>
          <div class="btn-container">
            <Button
              v-if="unit.showing_url || unit.agent_showing"
              @click="showAgentShowing"
              class="p-button-secondary mr-2 mb-2"
              label="Agent Showing"
            ></Button>
          </div>
          <div class="btn-container">
            <Button
              v-if="unit.virtual_agent_url"
              label="Virtual Agent Showing"
              @click="showVirtualAgent"
              class="p-button-secondary mb-2"
            />
          </div>
        </div>
      </div>
      <div class="availability-container">
        <span class="mb-2"
          >Available:
          {{
            new Date() >= availableOn
              ? "NOW"
              : availableOn.toLocaleString("en-US", {
                  dateStyle: "short",
                  timeZone: "UTC",
                })
          }}</span
        >
      </div>
    </div>

    <div class="btns-container">
      <a
        :href="`https://maxxpm.appfolio.com/listings/detail/${unit.rentable_uid}`"
        target="_blank"
        role="button"
        aria-label="view more unit details"
        tabindex="0"
      >
        <div class="left">
          <span link>More Details</span>
        </div>
      </a>
      <a
        :href="`https://maxxpm.appfolio.com/listings/rental_applications/new?listable_uid=${unit.rentable_uid}&source=${source}`"
        target="_blank"
        role="button"
        aria-label="open rental application"
        tabindex="0"
        @click="trackApplyNow"
      >
        <div class="right">
          <span>Apply Now</span>
        </div>
      </a>
    </div>
  </div>
  <Dialog
    v-model:visible="displayRentlyShowing"
    style="width: 95%"
    modal
    contentStyle="overflow: hidden; padding: 0;"
    :header="unit.unit_full_address"
  >
    <iframe
      :src="
        showingType === 'agent'
          ? `https://homes.rently.com/homes-for-rent/properties/${unit.rently_id}?fromsearch=true&btnTxt=SCHEDULE AGENT SHOWING&companyID=18705&source=iframe`
          : showingType === 'self'
          ? `https://homes.rently.com/homes-for-rent/properties/${unit.rently_id}?fromsearch=true&btnTxt=ENTER PROPERTY YOURSELF&companyID=18705&source=iframe`
          : ''
      "
      frameborder="0"
      style="width: 100%; height: 95vh; border-radius: 8px"
      id="rentlyShowFrame"
      @load="frameLoaded"
    ></iframe>
  </Dialog>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { event } from "vue-gtag";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Galleria from "primevue/galleria";

//eslint-disable-next-line
const props = defineProps({ unit: Object, source: { type: String, default: "maxxpm.com" } });

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const showImages = ref(false);

const responsiveOptions = ref([
  {
    breakpoint: "1500px",
    numVisible: 5,
  },
  {
    breakpoint: "1024px",
    numVisible: 3,
  },
  {
    breakpoint: "768px",
    numVisible: 2,
  },
]);

const Calendly = ref(null);

function isCalendlyEvent(e) {
  return e.origin === "https://calendly.com" && e.data.event && e.data.event.indexOf("calendly.") === 0;
}

function calendlyMessageHandler(e) {
  if (isCalendlyEvent(e)) {
    switch (e.data.event) {
      case "calendly.event_type_viewed":
        event("agent_showing_viewed", {
          event_category: "tenants",
          event_label: props.unit.appfolio_id,
        });
        break;
      case "calendly.date_and_time_selected":
        event("agent_showing_date_time_selected", {
          event_category: "tenants",
          event_label: props.unit.appfolio_id,
        });
        break;
      case "calendly.event_scheduled":
        event("agent_showing_booked", {
          event_category: "tenants",
          event_label: e.data.payload,
        });
        break;
    }
  }
}

const calendlyObserver = new MutationObserver((mutationList) => {
  for (const mutation of mutationList) {
    if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
      const added = Array.from(mutation.addedNodes);
      const removed = Array.from(mutation.removedNodes);
      if (added.length > 0 && added.some((node) => typeof node.classList !== "undefined" && node.classList.contains("calendly-overlay"))) {
        window.addEventListener("message", calendlyMessageHandler);
      }

      if (removed.length > 0 && removed.some((node) => typeof node.classList !== "undefined" && node.classList.contains("calendly-overlay"))) {
        window.removeEventListener("message", calendlyMessageHandler);
        calendlyObserver.disconnect();
      }
    }
  }
});

onMounted(() => {
  Calendly.value = window.Calendly;
});

onBeforeUnmount(() => {
  window.removeEventListener("message", calendlyMessageHandler);
  calendlyObserver.disconnect();
});

const availableOn = computed(() => new Date(props.unit.available_on));
const displayRentlyShowing = ref(false);
const showingType = ref();

function frameLoaded() {
  const content = document.querySelector(".p-dialog-content");
  const frame = document.querySelector("#rentlyShowFrame");
  frame.style.height = content.clientHeight + "px";
}

function trackApplyNow() {
  event("apply_now", {
    event_category: "tenants",
    event_label: props.unit.appfolio_id,
  });
}

function showAgentShowing() {
  if (props.unit.showing_url) {
    calendlyObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    Calendly.value.initPopupWidget({
      url: props.unit.showing_url,
    });
  } else if (props.unit.agent_showing) {
    displayRentlyShowing.value = true;
    showingType.value = "agent";
  }
}

function showVirtualAgent() {
  if (props.unit.virtual_agent_url) {
    calendlyObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    Calendly.value.initPopupWidget({
      url: props.unit.virtual_agent_url,
    });
  }
}

function trackSelfShowing() {
  displayRentlyShowing.value = true;
  showingType.value = "self";

  event("self_showing_viewed", {
    event_category: "tenants",
    event_label: props.unit.appfolio_id,
  });
}
</script>

<style scoped>
.listing {
  margin: 1rem 0.5rem;
  flex: 0 0 48%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: stretch;
}

.unit-listing {
  border: 1px solid #bbb;
  background: #fafafa;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  border-bottom: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1rem;
  flex-wrap: wrap;
  align-items: center;
  flex: 1 1 auto;
}

.details-container,
.address-container {
  flex: 1 1 auto;
}

.address-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.showing-btns {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.btn-container {
  margin: 0 0.5rem;
}

.details-container {
  display: flex;
  flex-direction: column;
}

.unit-details > span {
  padding: 0.25rem;
}

.unit-details {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  margin-bottom: 0.5rem;
}

.fa-detail {
  color: var(--primary-color);
  margin-right: 0.5rem;
}

.btns-container {
  display: flex;
  justify-content: space-around;
  align-items: center;
  border: 1px solid #bbb;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
}

.btns-container > a {
  flex: 1 1 auto;
}

/* .btns-container .left,
.btns-container .right {
  padding: 0.5rem;
} */

.btns-container .right {
  border-bottom-right-radius: 4px;
  width: 100%;
}

.btns-container .left {
  border-bottom-left-radius: 4px;
  width: 100%;
  border-right: 1px solid #bbb;
}

.btns-container > a {
  text-decoration: none;
}

.left,
.right {
  padding: 0.75rem;
}

.right {
  background-color: var(--primary-color);
}

/* .left > span,
.right > span {
} */

.right > span {
  width: 100%;
  border-bottom-left-radius: 0;
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  border-bottom-right-radius: 4px;
  border: none;
  color: white;
}

.left > span {
  width: 100%;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  border: none;
  color: var(--primary-color);
}

.left:hover {
  background-color: #fafafa;
}

.right:hover {
  background-color: color-mix(in srgb, var(--primary-color) 75%, black);
}

.unit-title {
  margin: 1rem 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.img-container {
  position: relative;
}

.mask-icon-group {
  display: flex;
  border-radius: 20px;
  border: 1px solid white;
  justify-content: center;
  align-items: center;
}

.mask-icon {
  flex: 0 0 auto;
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  color: black;
  background-color: white;
  padding: 0.35rem 0.35rem 0.35rem 0.7rem;
}

.mask-text {
  flex: 1 1 auto;
  color: white;
  padding: 0.35rem 0.7rem 0.35rem 0.35rem;
}

.mask-text > p {
  margin: 0;
}

.unit-img {
  max-width: 400px;
}

.img-mask {
  position: absolute;
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.65);
  height: 30%;
  top: 70%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.img-mask:hover {
  cursor: pointer;
}

@media (max-width: 1600px) {
  .listing {
    flex: 0 0 45%;
  }
}

@media (max-width: 1400px) {
  .listing {
    flex: 1 1 auto;
  }
}

@media (max-width: 1200px) {
  .listing {
    flex: 0 0 48%;
  }
}

@media (max-width: 800px) {
  .listing {
    flex: 1 1 auto;
  }
}
</style>
